import React, { useContext } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { ColorStyle } from "src/utils/Colors";
import styles from "./AiFieldFilledHint.module.css";

interface AiFieldFilledHintProps {
    /**
     * INVOICE_PROPERTY key of the form field to hint for — one of the
     * QR-fallback fields the AI mapper can fill (amount, invoiceNumber,
     * date, dueDate) or the auto-applied match fields (realestate, creditor).
     */
    field: string;
    /**
     * When true, renders in normal flow (adds vertical space) instead of the
     * default absolute-below-input positioning. Use where the input has
     * following siblings that would visually clash with an overlay — e.g.
     * the creditor field whose selected-creditor info rows sit directly
     * beneath the search input.
     */
    inFlow?: boolean;
}

/**
 * Green "Mit KI erkannt" hint shown directly under a form input whose current
 * value came from the AI fallback path (mapAiDocumentFallbackOnCurrentInvoice).
 * The hint disappears the moment the user edits the field — the Invoice's
 * updateProperty clears the per-field flag on any form-driven change.
 */
const AiFieldFilledHintBase = ({ field, inFlow = false }: AiFieldFilledHintProps) => {
    const { t } = useTranslation();
    const { invoiceStore } = useContext(RootStoreContext);

    const isFilled = !!invoiceStore.currentInvoice?.aiFilledFields?.[field];

    if (!isFilled) {
        return null;
    }

    return (
        <div className={inFlow ? styles.AiFieldFilledHintFlow : styles.AiFieldFilledHint}>
            <UI.Icon icon={UI.SVGIcon.Check} size={14} color={ColorStyle("green")} />
            <span>{t("screens.kredi_flow.ai.field_ai_filled")}</span>
        </div>
    );
};

export const AiFieldFilledHint = observer(AiFieldFilledHintBase);
